type Plain = Record<string, unknown>;

const EMPTY_LABEL = '—';

function isPlainObject(value: unknown): value is Plain {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
}

function joinParts(parts: Array<string | undefined>, separator = ' · '): string {
    return parts.filter((part): part is string => Boolean(part && part.trim())).join(separator);
}

function formatIsoDate(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function formatAddress(address: Plain): string {
    const fullAddress = text(address.fullAddress);
    if (fullAddress) return fullAddress;
    const street = joinParts([text(address.street), text(address.number)], ', ');
    return joinParts([street, text(address.neighborhood), joinParts([text(address.city), text(address.state)], '/')], ' – ');
}

function formatBankAccount(account: unknown): string {
    if (!isPlainObject(account)) return '';
    const digit = text(account.digit);
    const accountNumber = joinParts([text(account.account), digit], '-');
    return joinParts([
        text(account.bankCode) && `Banco ${text(account.bankCode)}`,
        text(account.agency) && `Ag ${text(account.agency)}`,
        accountNumber && `Conta ${accountNumber}`,
        text(account.pixKey) && `PIX ${text(account.pixKey)}`,
    ]);
}

function formatChild(child: Plain): string {
    return joinParts([text(child.name), formatIsoDate(text(child.birthDate))], ' · ');
}

function formatEducation(education: Plain): string {
    return joinParts([text(education.type), text(education.institution)], ' · ');
}

function formatEmergencyContact(contact: Plain): string {
    return joinParts([text(contact.name), text(contact.phone), text(contact.relationship)]);
}

export function formatFieldValue(field: string, value: unknown): string[] {
    if (value === null || value === undefined) return [EMPTY_LABEL];

    switch (field) {
        case 'addresses': {
            const list = Array.isArray(value) ? value.filter(isPlainObject).map(formatAddress).filter(Boolean) : [];
            return list.length > 0 ? list : [EMPTY_LABEL];
        }
        case 'isMailingSameAsMain':
            return [value === true ? 'Sim' : 'Não'];
        case 'children': {
            const list = Array.isArray(value) ? value.filter(isPlainObject).map(formatChild).filter(Boolean) : [];
            return list.length > 0 ? list : [EMPTY_LABEL];
        }
        case 'educations': {
            const list = Array.isArray(value) ? value.filter(isPlainObject).map(formatEducation).filter(Boolean) : [];
            return list.length > 0 ? list : [EMPTY_LABEL];
        }
        case 'salaryAccount':
        case 'personalAccount':
            return [formatBankAccount(value) || EMPTY_LABEL];
        case 'emergencyContact':
            return [isPlainObject(value) ? formatEmergencyContact(value) || EMPTY_LABEL : EMPTY_LABEL];
        default:
            return [text(value) || EMPTY_LABEL];
    }
}

export function summarizeGroup(groupId: string, values: Record<string, unknown>): string {
    switch (groupId) {
        case 'address': {
            const first = formatFieldValue('addresses', values.addresses)[0];
            return first && first !== EMPTY_LABEL ? first : 'Não cadastrado';
        }
        case 'contact': {
            const parts = [text(values.personalEmail), text(values.phone)].filter(Boolean);
            return parts.length > 0 ? parts.join(' · ') : 'Não cadastrado';
        }
        case 'maritalStatus': {
            const status = text(values.maritalStatus);
            if (!status) return 'Não informado';
            const spouse = text(values.spouseName);
            return spouse ? `${status} · ${spouse}` : status;
        }
        case 'children': {
            const count = Array.isArray(values.children) ? values.children.length : 0;
            return count === 1 ? '1 filho cadastrado' : `${count} filhos cadastrados`;
        }
        case 'bankAccount': {
            const salary = formatFieldValue('salaryAccount', values.salaryAccount)[0];
            return salary && salary !== EMPTY_LABEL ? salary : 'Não cadastrada';
        }
        case 'education': {
            const count = Array.isArray(values.educations) ? values.educations.length : 0;
            return count === 1 ? '1 formação cadastrada' : `${count} formações cadastradas`;
        }
        default:
            return '';
    }
}
